import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { ScrollPanel } from 'primereact/scrollpanel';

import './ChatBox.css';

const ChatBox = () => {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const toast = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollPanelRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_PATH || 'https://localhost:7000/api';

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
    // Poll for new messages every 10 seconds
    const interval = setInterval(() => {
      fetchContacts();
      if (selectedContact) {
        fetchMessages(selectedContact.id);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedContact]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}whatsapp/contacts`);
      console.log('Contacts fetched:', response.data);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch contacts',
        life: 3000
      });
    }
  };

  const fetchMessages = async (contactId) => {
    if (!contactId) {
      console.warn('No contactId provided for fetchMessages');
      return;
    }
    
    console.log('Fetching messages for contactId:', contactId);
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API_BASE_URL}whatsapp/messages`, {
        params: { contactId }
      });
      console.log('Messages fetched:', response.data);
      
      // Map the response to ensure consistent property names
      const mappedMessages = response.data.map(msg => ({
        id: msg.id,
        messageId: msg.messageId || msg.messageid,
        from: msg.from || msg.from_ph,
        to: msg.to || msg.to_ph,
        body: msg.body,
        timestamp: msg.timestamp,
        type: msg.type,
        isRead: msg.isRead || msg.isread
      }));
      
      setMessages(mappedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch messages',
        life: 3000
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleContactSelect = (contact) => {
    console.log('Selected contact:', contact);
    console.log('Contact ID:', contact.id);
    console.log('Contact PhoneNumber:', contact.phoneNumber || contact.phonenumber);
    
    setSelectedContact(contact);
    if (contact && contact.id) {
      fetchMessages(contact.id);
    } else {
      console.warn('Contact has no ID:', contact);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;

    setSending(true);
    try {
      const response = await axios.post(`${API_BASE_URL}whatsapp/send`, {
        to: selectedContact.phoneNumber || selectedContact.phonenumber,
        text: newMessage.trim(),
        businessPhoneNumberId: process.env.REACT_APP_BUSINESS_PHONE_NUMBER_ID
      });

      console.log('Send message response:', response.data);

      if (response.status === 200) {
        setNewMessage('');
        // Refresh messages after sending
        await fetchMessages(selectedContact.id);
        // Update contacts to show latest message
        await fetchContacts();
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Message sent successfully',
          life: 2000
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.error || 'Failed to send message',
        life: 3000
      });
    } finally {
      setSending(false);
    }
  };

  // FIXED: Scroll to bottom function
  const scrollToBottom = () => {
    // Try using messagesEndRef first
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      return;
    }

    // Fallback: try to scroll the container
    if (scrollPanelRef.current) {
      try {
        // PrimeReact ScrollPanel might have different methods
        const panel = scrollPanelRef.current;
        if (panel.scrollTop !== undefined) {
          panel.scrollTop = panel.scrollHeight;
        } else if (panel.getContainer) {
          const container = panel.getContainer();
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        }
      } catch (error) {
        console.warn('Error scrolling:', error);
        // Last resort: find the scrollable container
        const container = document.querySelector('.p-scrollpanel .p-scrollpanel-content');
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return '';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const today = new Date();
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      }
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      }
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Check if contact has data
  const hasContactData = (contact) => {
    return contact && contact.id && (contact.phoneNumber || contact.phonenumber);
  };

  return (
    <div className="chat-container">
      <Toast ref={toast} />
      
      <div className="chat-wrapper">
        {/* Contacts Sidebar */}
        <div className="contacts-sidebar">
          <div className="contacts-header">
            <h2>WhatsApp Chats</h2>
            <div className="contacts-count">
              <Badge value={contacts.length} severity="info" />
            </div>
          </div>
          
          <Divider />
          
          <ScrollPanel style={{ height: 'calc(100% - 80px)' }}>
            {contacts.length === 0 ? (
              <div className="no-contacts">
                <i className="pi pi-whatsapp" style={{ fontSize: '3rem', color: '#25D366' }}></i>
                <p>No contacts yet</p>
              </div>
            ) : (
              contacts.map((contact) => (
                <div
                  key={contact.id || contact.phoneNumber || contact.phonenumber}
                  className={`contact-item ${selectedContact?.id === contact.id ? 'active' : ''}`}
                  onClick={() => handleContactSelect(contact)}
                >
                  <Avatar 
                    label={getInitials(contact.name)} 
                    shape="circle" 
                    size="large"
                    style={{ backgroundColor: '#25D366', color: '#ffffff' }}
                  />
                  <div className="contact-info">
                    <div className="contact-name">{contact.name || contact.phoneNumber || contact.phonenumber}</div>
                    <div className="contact-last-message">
                      {contact.lastMessage || contact.lastmessage || 'No messages yet'}
                    </div>
                  </div>
                  <div className="contact-time">
                    {(contact.lastMessageTime || contact.lastmessagetime) && 
                      formatTime(contact.lastMessageTime || contact.lastmessagetime)}
                  </div>
                </div>
              ))
            )}
          </ScrollPanel>
        </div>

        {/* Chat Area */}
        <div className="chat-area">
          {!selectedContact || !hasContactData(selectedContact) ? (
            <div className="no-chat-selected">
              <i className="pi pi-comments" style={{ fontSize: '4rem', color: '#ccc' }}></i>
              <h3>Select a contact to start chatting</h3>
              <p>Choose a contact from the sidebar</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header-info">
                  <Avatar 
                    label={getInitials(selectedContact.name)} 
                    shape="circle" 
                    size="large"
                    style={{ backgroundColor: '#25D366', color: '#ffffff' }}
                  />
                  <div className="chat-header-details">
                    <div className="chat-header-name">
                      {selectedContact.name || selectedContact.phoneNumber || selectedContact.phonenumber}
                    </div>
                    <div className="chat-header-phone">
                      {selectedContact.phoneNumber || selectedContact.phonenumber}
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages Area */}
              <div className="messages-area">
                <ScrollPanel ref={scrollPanelRef} style={{ height: '100%' }}>
                  {loadingMessages ? (
                    <div className="loading-messages">
                      <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                      <p>Loading messages...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="no-messages">
                      <i className="pi pi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                      <p>No messages yet</p>
                      <p className="no-messages-sub">Send a message to start the conversation</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => {
                        const showDate = index === 0 || 
                          (message.timestamp && messages[index - 1]?.timestamp && 
                          new Date(message.timestamp).toDateString() !== 
                          new Date(messages[index - 1].timestamp).toDateString());
                        
                        // Check if this is an incoming message (from contact)
                        const isFromBusiness = message.from === 'BUSINESS';
                        const isOutgoing = isFromBusiness || message.type === 'OUT' || message.type === 'sent';
                        
                        return (
                          <React.Fragment key={message.id || index}>
                            {showDate && (
                              <div className="message-date-divider">
                                <span>{formatDate(message.timestamp)}</span>
                              </div>
                            )}
                            <div className={`message ${isOutgoing ? 'message-out' : 'message-in'}`}>
                              <div className="message-bubble">
                                <div className="message-text">{message.body || 'No content'}</div>
                                <div className="message-time">
                                  {formatTime(message.timestamp)}
                                  {isOutgoing && (
                                    <i className={`pi ${message.isRead ? 'pi-check-circle' : 'pi-check'} message-status`}></i>
                                  )}
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      {/* Add an empty div for scrolling reference */}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </ScrollPanel>
              </div>

              {/* Message Input */}
              <div className="message-input-area">
                <div className="message-input-wrapper">
                  <InputTextarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    rows={1}
                    className="message-input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={sending}
                  />
                  <Button
                    icon="pi pi-send"
                    className="send-button"
                    onClick={sendMessage}
                    loading={sending}
                    disabled={!newMessage.trim() || sending}
                    rounded
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatBox;